import { Checkbox, Input, PhoneInput } from '@wonderschool/common-base-ui';
import { useCallback, useMemo } from 'react';
import { Controller, ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Validator from 'validator';
import { FormComponentProps } from '../../../common';
import { canSendInvitation, useContacts } from '../../../contacts';
import { ParentType, StudentAndParentsType } from '../../../students/types';

type EnrollmentParentEditFormProps = FormComponentProps<ParentType> & {
  parent?: ParentType;
  parents?: ParentType[];
  form: UseFormReturn<StudentAndParentsType>;
  parentIndex: number;
};

function EnrollmentParentEditForm({
  title,
  hasTitle = true,
  parent: parentParam,
  parents = [],
  form,
  parentIndex,
}: EnrollmentParentEditFormProps) {
  const { t } = useTranslation();
  const { contacts } = useContacts();
  const {
    control,
    formState: { errors },
    watch,
  } = form;

  const parent = watch(`parents.${parentIndex}`);

  const canSendInvitationToParent = useCallback((parentLocal: ParentType): boolean => {
    return !parentLocal.contact || (parentLocal.contact && canSendInvitation(parentLocal.contact));
  }, []);

  const shouldShowSendInviteCheckbox = useMemo<boolean>(() => {
    return canSendInvitationToParent(parent);
  }, [parent, canSendInvitationToParent]);

  const isEmailInUse = useCallback(
    (email: string): boolean => {
      if (!email) return false;

      const contact = contacts.find((contact: any) => contact.email === email && !parent.contact?.id);
      if (contact) return true;

      const parentFound = parents.find(
        (p: ParentType) =>
          p.email === email && p.uuid !== parent.uuid && !parent.contact?.id && !parent.isInterestedFamily
      );
      return !!parentFound;
    },
    [contacts, parent, parents]
  );

  const viewTitle = hasTitle ? t(title || 'students.newParentFormTitle') : '';

  const handlePhoneChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<StudentAndParentsType, `parents.${number}.phone`>
  ) => {
    field.onChange(event.target.value);
  };

  return (
    <div className="parent-edit-form" data-testid="parent-edit-form">
      {!!viewTitle && (
        <h2 className="mb-4 text-2xl font-bold" data-testid="parent-edit-title">
          {viewTitle}
        </h2>
      )}
      <div className="-mx-2 flex flex-wrap">
        <div className="mb-4 w-full px-2 md:w-1/2">
          <Controller
            name={`parents.${parentIndex}.firstName`}
            control={control}
            rules={{ required: t('common.firstNameRequired') }}
            render={({ field }) => (
              <Input
                {...field}
                required
                type="text"
                placeholder={t('common.firstName')}
                label={t('common.firstName')}
                data-testid="parent-first-name"
                className="w-full rounded border p-2"
                error={errors.parents?.[parentIndex]?.firstName?.message}
              />
            )}
          />
        </div>
        <div className="mb-4 w-full px-2 md:w-1/2">
          <Controller
            name={`parents.${parentIndex}.lastName`}
            control={control}
            rules={{ required: t('common.lastNameRequired') }}
            render={({ field }) => (
              <Input
                {...field}
                required
                type="text"
                placeholder={t('common.lastName')}
                label={t('common.lastName')}
                data-testid="parent-last-name"
                className="w-full rounded border p-2"
                error={errors.parents?.[parentIndex]?.lastName?.message}
              />
            )}
          />
        </div>
      </div>
      <div className="-mx-2 flex flex-wrap">
        <div className="mb-4 w-full px-2 md:w-1/2">
          <Controller
            name={`parents.${parentIndex}.email`}
            control={control}
            rules={{
              required: watch(`parents.${parentIndex}.shouldSendInvitation`) ? t('common.emailRequired') : false,
              validate: (value) => {
                if (!watch(`parents.${parentIndex}.shouldSendInvitation`)) {
                  return true;
                }

                if (!Validator.isEmail(value || '')) {
                  return t('common.invalidEmail');
                }

                return !isEmailInUse(value) || t('students.emailExistsInContacts');
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                required={watch(`parents.${parentIndex}.shouldSendInvitation`)}
                type="email"
                placeholder={t('common.email')}
                label={t('common.email')}
                data-testid="parent-email"
                className="w-full rounded border p-2"
                error={errors.parents?.[parentIndex]?.email?.message}
              />
            )}
          />
        </div>
        <div className="mb-4 w-full px-2 md:w-1/2">
          <Controller
            name={`parents.${parentIndex}.phone`}
            control={control}
            render={({ field }) => (
              <PhoneInput
                placeholder={t('common.phone')}
                label={t('common.phone')}
                data-testid="parent-phone"
                value={field.value}
                onChange={(value) => handlePhoneChange(value, field)}
                error={errors.parents?.[parentIndex]?.phone?.message}
              />
            )}
          />
        </div>
      </div>
      {shouldShowSendInviteCheckbox ? (
        <div className="mb-4">
          <Controller
            name={`parents.${parentIndex}.shouldSendInvitation`}
            control={control}
            render={({ field }) => (
              <Checkbox
                {...field}
                checked={field.value}
                label={t('students.shouldSendInviteLabel')}
                data-testid="send-invitation-checkbox"
              />
            )}
          />
          <p className="mt-1 text-sm text-gray-600">{t('students.shouldSendInviteNote')}</p>
        </div>
      ) : (
        <div className="mb-4">
          <p className="mt-1 text-sm text-gray-600">{t('students.parentAlreadyVerified')}</p>
        </div>
      )}
    </div>
  );
}

export default EnrollmentParentEditForm;
